import { useState, useEffect } from "react";
import {
  Search, BookOpen, Rocket, Globe, Share2, Coins, Users, Package,
  UtensilsCrossed, BarChart2, Heart, ChevronDown, ChevronRight,
  HelpCircle, Mail, MessageCircleQuestion, Sparkles, Play,
  MousePointerClick, ArrowRight, CheckCircle2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import api from "../lib/api";

const ICON_MAP = {
  rocket: Rocket,
  "book-open": BookOpen,
  "utensils-crossed": UtensilsCrossed,
  package: Package,
  globe: Globe,
  "share-2": Share2,
  coins: Coins,
  users: Users,
  heart: Heart,
  "bar-chart-2": BarChart2,
};

export default function HelpPage() {
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    api.get(`/help/articles${search ? `?q=${encodeURIComponent(search)}` : ""}`)
      .then(data => { setArticles(data.articles || []); setFaqs(data.faqs || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search]);

  // Group articles by category
  const categories = {};
  articles.forEach(a => {
    if (!categories[a.category]) categories[a.category] = { label: a.category_label, articles: [] };
    categories[a.category].articles.push(a);
  });

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="help-page">
      {/* Header */}
      <div className="text-center pt-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
          <HelpCircle className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-2xl font-bold">Centro de Ayuda</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">
          Encuentra respuestas rapidas, guias paso a paso y aprende a sacar el maximo partido a MenuGenius
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-lg mx-auto">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => { setSearch(e.target.value); setLoading(true); }}
          placeholder="Buscar en la ayuda... (ej. menu, creditos, web)"
          data-testid="help-search"
          className="pl-10 h-11 rounded-full border-border"
        />
      </div>

      {/* Quick access cards */}
      {!search && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { icon: Rocket, label: "Primeros Pasos", cat: "primeros-pasos", color: "text-emerald-500 bg-emerald-500/10" },
            { icon: BookOpen, label: "Menus", cat: "menus", color: "text-blue-500 bg-blue-500/10" },
            { icon: Globe, label: "Web", cat: "web", color: "text-purple-500 bg-purple-500/10" },
            { icon: Share2, label: "Redes Sociales", cat: "redes-sociales", color: "text-pink-500 bg-pink-500/10" },
            { icon: Coins, label: "Suscripcion", cat: "suscripcion", color: "text-amber-500 bg-amber-500/10" },
          ].map(c => (
            <button
              key={c.cat}
              onClick={() => {
                const el = document.getElementById(`cat-${c.cat}`);
                el?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid={`quick-${c.cat}`}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border
                         hover:border-primary/30 hover:shadow-sm transition-all text-center"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.color}`}>
                <c.icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-medium">{c.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* FAQs */}
      {faqs.length > 0 && (
        <div data-testid="faqs-section">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <MessageCircleQuestion className="w-5 h-5 text-primary" />
            Preguntas frecuentes
          </h2>
          <div className="space-y-1.5">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  data-testid={`faq-${i}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                >
                  <span className="text-sm font-medium pr-4">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${expandedFaq === i ? "rotate-180" : ""}`} />
                </button>
                {expandedFaq === i && (
                  <div className="px-4 pb-3 text-sm text-muted-foreground border-t border-border pt-2">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Articles by category */}
      {Object.entries(categories).map(([catKey, cat]) => (
        <div key={catKey} id={`cat-${catKey}`} data-testid={`category-${catKey}`}>
          <h2 className="text-lg font-bold mb-3">{cat.label}</h2>
          <div className="space-y-2">
            {cat.articles.map(article => {
              const Icon = ICON_MAP[article.icon] || HelpCircle;
              const isExpanded = expandedArticle === article.id;
              return (
                <div key={article.id} className="border border-border rounded-xl overflow-hidden" data-testid={`article-${article.id}`}>
                  <button
                    onClick={() => setExpandedArticle(isExpanded ? null : article.id)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4.5 h-4.5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{article.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{article.summary}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-border pt-3">
                      {/* Animated visual walkthrough */}
                      <div className="relative">
                        <div className="absolute left-[19px] top-5 bottom-5 w-0.5 bg-gradient-to-b from-primary/40 via-primary/20 to-transparent" />
                        <ol className="space-y-4">
                          {article.steps.map((step, i) => (
                            <li
                              key={i}
                              className="flex gap-3.5 relative"
                              style={{ animation: `stepFadeIn 0.3s ease-out ${i * 0.08}s both` }}
                            >
                              <div className="w-10 h-10 rounded-xl bg-primary/10 border-2 border-primary/20
                                            flex items-center justify-center flex-shrink-0 z-10 relative">
                                <span className="text-xs font-bold text-primary">{i + 1}</span>
                              </div>
                              <div className="flex-1 pt-1.5">
                                <p className="text-sm leading-relaxed text-foreground">{step}</p>
                                {i < article.steps.length - 1 && (
                                  <div className="flex items-center gap-1 mt-1.5 text-[10px] text-muted-foreground/60">
                                    <ArrowRight className="w-3 h-3" /> Siguiente paso
                                  </div>
                                )}
                                {i === article.steps.length - 1 && (
                                  <div className="flex items-center gap-1.5 mt-2 text-xs text-emerald-500 font-medium">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Listo!
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* No results */}
      {search && articles.length === 0 && faqs.length === 0 && !loading && (
        <div className="text-center py-12">
          <Search className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No se encontraron resultados para "{search}"</p>
          <p className="text-xs text-muted-foreground mt-1">Prueba con otras palabras o pregunta al asistente IA</p>
        </div>
      )}

      {/* Contact support */}
      <div className="bg-muted/30 border border-border rounded-xl p-6 text-center">
        <Sparkles className="w-8 h-8 text-primary/40 mx-auto mb-3" />
        <h3 className="font-semibold text-sm mb-1">No encuentras lo que buscas?</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Usa el asistente IA en la esquina inferior derecha o contacta con nuestro equipo
        </p>
        <a
          href="mailto:support@menugenius.online"
          data-testid="contact-support-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-medium
                     hover:opacity-90 transition-opacity"
        >
          <Mail className="w-3.5 h-3.5" /> Contactar soporte
        </a>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes stepFadeIn {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
